import React, { useState, useMemo } from 'react';
import './EditMailBoxes.scss';
import useTranslations from '../../../hooks/useTranslations';
import { IMailBox } from '../../../models/site';
import { ClosetDetails } from '../../../store/entities/configuration/type.d';
import { CellToCloset } from '../../../store/entities/configuration/type.d';
import { Box, FormControl } from '@mui/material';
import Title from '../../../components/UI/Title/Title';
import TextInput from '../../../components/UI/TextInput/TextInput';
import Button from '../../../components/UI/Button/Button';
import MailBoxesTable from './MailBoxesTable/MailBoxesTable';
import Alert from '../../../components/UI/Alert/Alert';

interface EditMailBoxesProps {
  closetDetails: ClosetDetails | null;
  mailBoxes: IMailBox[] | undefined;
  setMailBoxes: React.Dispatch<React.SetStateAction<IMailBox[]>>;
}

const EditMailBoxes: React.FC<EditMailBoxesProps> = ({
  closetDetails,
  mailBoxes,
  setMailBoxes,
}) => {
  const { rcTranslate } = useTranslations();
  const [startPosition, setStartPosition] = useState<number | undefined>();
  const [endPosition, setEndPosition] = useState<number | undefined>();
  const [startMailBoxNumber, setStartMailBoxNumber] = useState<string>();
  const [error, setError] = useState<string>();

  const cells: CellToCloset[] = closetDetails?.cellFormations || [];
  const existingPositionsSet = useMemo(
    () => new Set(cells.map((c) => c.position.toString())),
    [cells]
  );

  const buildPositions = (): number[] => {
    if (!startPosition) return [];
    const endPos = endPosition ?? startPosition;

    const positions: number[] = [];
    const start = Math.min(startPosition, endPos);
    const end = Math.max(startPosition, endPos);

    for (let p = start; p <= end; p++) {
      positions.push(p);
    }
    return positions;
  };

  const addAndValidateRange = () => {
    setError('');
    const positions = buildPositions();
    if (!positions.length) {
      setError(
        rcTranslate('sites.closetWithSiteDetails.mailBoxes.emptyRangeError')
      );
      return;
    }
    const startMB = Number(startMailBoxNumber);
    if (isNaN(startMB)) {
      setError(
        rcTranslate(
          'sites.closetWithSiteDetails.mailBoxes.startMailboxRequiredError'
        )
      );
      return;
    }
    const missing = positions.filter(
      (p) => !existingPositionsSet.has(p.toString())
    );
    if (missing.length) {
      setError(
        rcTranslate(
          'sites.closetWithSiteDetails.mailBoxes.missingPositionsError'
        ) +
          ': ' +
          missing.join(', ')
      );
      return;
    }
    const filtered = (mailBoxes || []).filter(
      (mb) => !positions.includes(Number(mb.position))
    );
    const created: IMailBox[] = positions.map((p, idx) => ({
      position: p,
      mailBoxNumber: (startMB + idx).toString(),
    }));
    setMailBoxes(
      [...filtered, ...created].sort(
        (a, b) => Number(a.position) - Number(b.position)
      )
    );
  };

  const clearAll = () => {
    setMailBoxes((prev) => prev.map((mb) => ({ ...mb, mailBoxNumber: '' })));
    setError('');
  };

  const removeOne = (position: number | string) => {
    setMailBoxes((prev) => prev.filter((mb) => mb.position !== position));
  };

  const updateMailBoxNumber = (position: number | string, value: string) => {
    setMailBoxes((prev) =>
      prev.map((mb) =>
        mb.position === position ? { ...mb, mailBoxNumber: value } : mb
      )
    );
  };

  return (
    <Box className="mailBoxesEditor">
      <Title text={'sites.closetWithSiteDetails.mailBoxes.editorTitle'} />
      <Box className="mailBoxesEditor__form">
        <FormControl size="small" fullWidth>
          <TextInput
            id="startPosition"
            label={rcTranslate(
              'sites.closetWithSiteDetails.mailBoxes.startPosition'
            )}
            value={startPosition}
            onChange={(e: any) => setStartPosition(e.target.value === '' ? undefined : Number(e.target.value))}
            type="number"
          />
          <TextInput
            id="endPosition"
            label={rcTranslate(
              'sites.closetWithSiteDetails.mailBoxes.endPosition'
            )}
            value={endPosition}
            onChange={(e: any) => setEndPosition(e.target.value === '' ? undefined : Number(e.target.value))}
            type="number"
          />
          <TextInput
            id="startMailBoxNumber"
            label={rcTranslate(
              'sites.closetWithSiteDetails.mailBoxes.startMailBoxNumber'
            )}
            value={startMailBoxNumber}
            onChange={(e: any) => setStartMailBoxNumber(e.target.value)}
          />
        </FormControl>
      </Box>
      <Box className="mailBoxesEditor__actions">
        <Button
          title={'sites.closetWithSiteDetails.mailBoxes.addRangeButton'}
          onClick={addAndValidateRange}
        />
        <Button
          title={'sites.closetWithSiteDetails.mailBoxes.clearAllButton'}
          onClick={clearAll}
          disabled={!mailBoxes?.length}
          outlined
          isCancelButton
        />
      </Box>
      {error && <Alert color="error" message={error} />}
      <MailBoxesTable
        mailBoxes={mailBoxes}
        rcTranslate={rcTranslate}
        updateMailBoxNumber={updateMailBoxNumber}
        removeOne={removeOne}
      />
    </Box>
  );
};

export default EditMailBoxes;
